<template>
  <RevForm
    :id="FORM_ID"
    class="my-12"
    :initial-values
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <RevInputDate
        id="start-date"
        v-model="values.startDate"
        class="mb-12"
        :error="errors.startDate"
        :label="i18n(translations.startDate)"
      />

      <RevInputDate
        id="end-date"
        v-model="values.endDate"
        class="mb-12"
        :error="errors.endDate"
        :label="i18n(translations.endDate)"
      />

      <RevInputSelect
        v-if="shouldShowEncoding"
        id="select-encoding"
        v-model="values.encoding"
        class="mb-12"
        :error="errors.encoding"
        :label="i18n(translations.encoding)"
        :options="encodingOptions"
      />

      <RevInputSelect
        id="order-state"
        v-model="values.state"
        class="mb-12"
        :error="errors.state"
        :label="i18n(translations.state)"
        :options="statesOptions"
      />

      <RevInputSelect
        id="back-ship"
        v-model="values.isBackship"
        class="mb-12"
        :error="errors.isBackship"
        :label="i18n(translations.isBackship)"
        :options="backshipOptions"
      />

      <InputSelectLanguage
        v-model="values.language"
        class="mb-12"
        :error="errors.language"
      />

      <RevInputText
        id="order-id"
        v-model="values.orderId"
        class="mb-12"
        :error="errors.orderId"
        :label="i18n(translations.orderId)"
        type="number"
      />

      <RevInputText
        id="sku"
        v-model="values.sku"
        class="mb-12"
        :error="errors.sku"
        :label="i18n(translations.sku)"
        type="text"
      />

      <RevInputText
        id="customer-first-name"
        v-model="values.customerFirstName"
        class="mb-12"
        :error="errors.customerFirstName"
        :label="i18n(translations.customerFirstName)"
        type="text"
      />

      <RevInputText
        id="customer-last-name"
        v-model="values.customerLastName"
        class="mb-12"
        :error="errors.customerLastName"
        :label="i18n(translations.customerLastName)"
        type="text"
      />

      <RevInputSelect
        id="market"
        v-model="values.market"
        class="mb-12"
        :label="i18n(translations.market)"
        :options="availableMarketsOptions"
      />

      <RevInputSelect
        id="carrier-id"
        v-model="values.carrierId"
        class="mb-12"
        :label="i18n(translations.carrierId)"
        :options="shippingProvidersOptions"
      />

      <RevInputSelect
        id="order-date-type"
        v-model="values.dateType"
        class="mb-12"
        :label="i18n(translations.dateType)"
        :options="dateTypeOptions"
      />
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputDate } from '@ds/components/InputDate'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { useFiltersOptions } from '../../../Tab/composables/useFiltersOptions'
import { FORM_ID, INITIAL_VALUES } from '../../ExportDrawer.constants'
import type { FormValues } from '../../ExportDrawer.types'

import translations from './Form.translations'

const emit = defineEmits(['submit'])
const props = defineProps<{ withEncoding?: boolean }>()

const currentLocale = useI18nLocale()
const i18n = useI18n()
const { isFeatureEnabled } = useBusinessFeaturesStore()

const {
  availableMarketsOptions,
  backshipOptions,
  dateTypeOptions,
  statesOptions,
  shippingProvidersOptions,
} = useFiltersOptions()

const encodingOptions = [
  { value: 'utf-8', label: 'UTF-8' },
  { value: 'latin1', label: 'Latin-1 (CP-1252)' },
]

const shouldShowEncoding = computed(
  () => props.withEncoding && isFeatureEnabled('multiEncodingEnabled'),
)

const initialValues = computed(() => ({
  ...INITIAL_VALUES,
  encoding: encodingOptions[0].value,
  language: currentLocale,
  endDate: new Date().toISOString().split('T')[0],
}))

const validate = makeValidate<FormValues>({
  startDate: required(i18n(translations.required)),
  endDate: required(i18n(translations.required)),
  ...(shouldShowEncoding.value && {
    enconding: required(i18n(translations.required)),
    language: required(i18n(translations.required)),
  }),
})

function handleSubmit(formValues: FormValues) {
  emit('submit', formValues)
}
</script>
